import { Link } from 'react-router-dom';
import CaseStudyPageNav from '../../../components/feature/CaseStudyPageNav';
import CaseStudyNav from '../../../components/feature/CaseStudyNav';
import { useSEO } from '../../../hooks/useSEO';
import JsonLd from '../../../components/feature/JsonLd';

export default function AustraliaPostMailDeliveryPage() {
  useSEO({
    title: 'Australia Post Mail Delivery Strategy | Design For',
    description: 'Strategic consulting for postal delivery operations. Research-driven strategy for logistics optimization and customer experience.',
    keywords: 'mail delivery, logistics optimization, postal services, customer experience, operations strategy',
    canonical: '/work/australia-post-mail-delivery',
    ogType: 'article',
    ogImage: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/ee9b8a579b0f0dfe46dd47075426190b.jpeg',
  });

  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Strategic consulting for postal delivery operations',
    description: 'We partnered with Australia Post to develop strategic frameworks for mail delivery operations, logistics optimization, and customer experience.',
    url: 'https://example.com/work/australia-post-mail-delivery',
    author: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    about: 'Postal service - Mail delivery strategy',
    keywords: 'mail delivery, logistics optimization, postal services, customer experience, operations strategy',
  };

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={jsonLdData} />
      <CaseStudyPageNav />

      {/* Main Content */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/work"
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-10"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Work
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: Text + Outcomes */}
            <div>
              <h1 className="text-4xl lg:text-5xl font-black text-black tracking-tight leading-tight mb-3">
                Changing history — how mail is delivered across Australia
              </h1>
              <h2 className="text-lg font-medium text-primary mb-8">Australia Post</h2>

              <p className="text-base text-gray-600 leading-relaxed mb-4">
                Australia Post faced a significant structural challenge: mail volumes had been declining for years, creating a projected loss of $750m. To respond, the organisation introduced a 2 speed letter service: a new 'regular' delivery timetable, alongside a higher priced 'priority' service that maintained existing speed but required an additional label.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-10">
                Given the scale and sensitivity of the change, customer confusion posed a major risk. We were engaged to apply a human-centred design approach to identify customer needs, test comprehension, and redesign communications to support behaviour change at scale.
              </p>

              <h3 className="text-xl font-black text-black mb-5">Outcomes</h3>
              <ul className="space-y-3">
                {[
                  'Ran 3 phases of customer experimentation, testing understanding, sentiment, and usability',
                  'Explored customer reactions to pricing, speed changes, and labelling requirements',
                  'Redesigned the priority label booklet using customer facing language and "just enough" information',
                  'Achieved a 100% success rate on the amended booklet design (up from 86%)',
                  'Delivered a consolidated messaging framework for consistent national communications',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base text-gray-700 leading-relaxed">
                    <span className="mt-2 w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Images */}
            <div className="flex flex-col gap-6">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/c5c40e9e56cca8dda124b128f6d36169.jpeg"
                  alt="Changing history — how mail is delivered across Australia"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/53423c35db8166115fdf9d635b71662b.jpeg"
                  alt="Australia Post priority label booklet redesign"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/1c718fb104ab77137e42b637182745c4.jpeg"
                  alt="Australia Post customer research session"
                  className="w-full h-auto object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CaseStudyNav currentSlug="australia-post-mail-delivery" />

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png"
                alt="Design For"
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">Strategic consulting for ambitious businesses.</p>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Navigate</h4>
              <div className="space-y-3">
                <Link to="/" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Home</Link>
                <Link to="/about" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">About</Link>
                <Link to="/services" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Services</Link>
                <Link to="/work" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Work</Link>
                <Link to="/resources" className="block text-white/70 hover:text-[#ff4858] transition-colors text-sm font-medium">Insights</Link>
                <Link to="/contact" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Follow</h4>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/kara-prosser-26960977/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</a>
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
